import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ScreenProps } from '../../app/routes';
import { useRouter } from '../../app/router';
import { useAuth } from '../../auth/AuthProvider';
import type { Problem } from '../../components/data-grid/problem';
import { useI18n } from '../../i18n/I18nProvider';
import {
  ApiError,
  loadDocumentTypes,
  loadDocuments,
  loadPeriods,
  localizedName,
  toDocumentRow,
} from './api';
import type { DocumentTypeDto } from './api';
import { DocumentsView } from './DocumentsView';
import { documentTotal, EMPTY_FILTERS } from './filters';
import type { DocumentFilters, DocumentPeriod, DocumentRow } from './filters';

const PAGE_SIZE = 50;

export default function DocumentsScreen(_props: ScreenProps): JSX.Element {
  const { authorizedFetch } = useAuth();
  const { language } = useI18n();
  const { navigate } = useRouter();

  const [types, setTypes] = useState<DocumentTypeDto[]>([]);
  const [periods, setPeriods] = useState<DocumentPeriod[]>([]);
  const [rows, setRows] = useState<DocumentRow[]>([]);
  const [filters, setFilters] = useState<DocumentFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [loadedTypes, loadedPeriods] = await Promise.all([
          loadDocumentTypes(authorizedFetch),
          loadPeriods(authorizedFetch),
        ]);
        if (!cancelled) {
          setTypes(loadedTypes);
          setPeriods(loadedPeriods);
        }
      } catch (error) {
        if (!cancelled) {
          setProblem(error instanceof ApiError ? error.problem : { status: 0, code: 'network', detail: String(error) });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch]);

  const load = useCallback(async () => {
    setLoading(true);
    setProblem(null);
    try {
      const documents = await loadDocuments(authorizedFetch, filters);
      const names = new Map(types.map((type) => [type.code, localizedName(type.nameI18n, language)]));
      setRows(
        documents.map((document) =>
          toDocumentRow(
            document,
            names.get(document.type) ?? document.type,
            documentTotal(document),
          ),
        ),
      );
    } catch (error) {
      setProblem(
        error instanceof ApiError
          ? error.problem
          : { status: 0, code: 'network', detail: error instanceof Error ? error.message : String(error) },
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, filters, language, types]);

  useEffect(() => {
    void load();
  }, [load, reloadToken]);

  const typeOptions = useMemo(
    () =>
      types.map((type) => ({
        code: type.code,
        name: localizedName(type.nameI18n, language),
      })),
    [types, language],
  );

  // There is no organisation endpoint in this flight; the periods carry the
  // org ids, so the org filter is built from the organisations we can name.
  const orgOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const period of periods) {
      if (period.orgId !== undefined && period.orgId !== '' && !seen.has(period.orgId)) {
        seen.set(period.orgId, period.orgId.slice(0, 8));
      }
    }
    return [...seen].map(([id, name]) => ({ id, name }));
  }, [periods]);

  return (
    <DocumentsView
      types={typeOptions}
      periods={periods}
      orgs={orgOptions}
      rows={rows}
      filters={filters}
      page={page}
      pageSize={PAGE_SIZE}
      problem={problem}
      loading={loading}
      onFiltersChange={(next) => {
        setFilters(next);
        setPage(1);
      }}
      onApply={() => setReloadToken((token) => token + 1)}
      onReset={() => {
        setFilters(EMPTY_FILTERS);
        setPage(1);
        setReloadToken((token) => token + 1);
      }}
      onPageChange={setPage}
      onOpen={(row) => navigate(`/documents/${row.id}`)}
      onNew={() => navigate('/documents/new')}
    />
  );
}
